import { supabase } from '@/integrations/supabase/client';
import { ChamadoAPI, ChamadosFiltros, STATUS_CHAMADOS, TIPOS_CHAMADOS } from '@/types/chamados';

const API_BASE_URL = 'https://api.prosperasistemas.com/v1';
const API_AUTH_TOKEN = 'Basic YTRkOGU2MTgtMzkxNy00MGVjLTg4NGEtNWQ3ODNlZmE0ODFjOg==';

interface SyncChamadosParams {
  filtros: ChamadosFiltros;
  campos?: string[];
  limite?: number;
}

interface FetchChamadosOptions {
  // Evita carregar JSON bruto da API (dados_api), que é pesado e impacta a tela de lista.
  lightweight?: boolean;
}

const CHAMADOS_LIGHTWEIGHT_SELECT = `
id,
chamado_id,
tipo_id,
tipo_descricao,
tipo_atendimento_id,
tipo_atendimento_descricao,
descricao,
natureza_id,
natureza_descricao,
status_id,
status_descricao,
prioridade_id,
prioridade_descricao,
prioridade_cor,
data_inclusao,
data_alteracao,
oculto,
data_conclusao,
data_inicio_atendimento,
chamado_vinculado_id,
chamado_vinculado_solicitacao,
empresa_id,
empresa_cnpj,
empresa_nome,
tempo_estimado,
horas_negociadas,
responsavel_id,
responsavel_nome,
projeto_id,
projeto_nome,
projeto_fechado,
projeto_categoria_id,
projeto_categoria_nome,
produto_id,
produto_nome,
produto_icone,
data_conclusao_prevista,
primeira_data_conclusao_prevista,
chamado_externo,
departamentos,
sla_executado,
sla_prevista,
sla_previstas,
sla_executados,
sla_porcentagem,
ultima_movimentacao_data,
ultima_movimentacao_diferenca,
ultima_movimentacao_string,
iniciado,
horas_executadas,
horas_faturadas,
data_ultima_alteracao_status,
ordens_servico,
solicitacao,
dados_api,
ultima_sincronizacao
`.replace(/\s+/g, '');

/** Gera o cURL completo da requisição de chamados para copiar e testar no Postman */
function buildCurlChamados(fullUrl: string): string {
  const headers = [
    ['accept', 'application/json, text/javascript, */*; q=0.01'],
    ['accept-language', 'pt-BR,pt;q=0.7'],
    ['authorization', API_AUTH_TOKEN],
    ['content-type', 'application/json'],
    ['origin', 'https://intranet.prosperasistemas.com'],
  ];
  const headerArgs = headers.map(([k, v]) => `-H '${k}: ${v}'`).join(' \\\n  ');
  return `curl -X GET '${fullUrl}' \\\n  ${headerArgs}`;
}

// Helper para extrair data de objetos ou strings
const extractDate = (value: any): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value.real) return value.real;
  return null;
};

export const syncChamadosFromAPI = async ({
  filtros,
  campos = [
    'produto',
    'empresa',
    'responsavel',
    'departamentos',
    'sla',
    'horasExecutadas',
    'dataInicioAtendimento',
    'ultimaMovimentacao',
    'projeto',
    'ordensServico',
    'solicitacao'
  ],
  limite = 5000
}: SyncChamadosParams) => {
  try {
    // Construir URL com filtros
    const url = new URL(`${API_BASE_URL}/chamados`);
    
    // A API retorna apenas abertos quando nenhum status é enviado.
    // Para sincronização completa, enviamos todos os status por padrão.
    const statusFiltro = filtros.status && filtros.status.length > 0 ? filtros.status : STATUS_CHAMADOS;
    statusFiltro.forEach(status => {
      url.searchParams.append('filtro[status][]', status);
    });
    
    // A API tende a retornar só "sustentacao" quando tipo não é informado.
    // Para sincronização completa, enviamos todos os tipos por padrão.
    const tipoFiltro = filtros.tipo && filtros.tipo.length > 0 ? filtros.tipo : [...TIPOS_CHAMADOS];
    tipoFiltro.forEach(tipo => {
      url.searchParams.append('filtro[tipo][]', tipo);
    });
    
    // Adicionar filtros de data de última alteração de status (formato AAAA-MM-DD)
    if (filtros.dataUltimaAlteracaoInicio) {
      url.searchParams.append('filtro[dataUltimaAlteracaoStatus][]', filtros.dataUltimaAlteracaoInicio);
    }
    
    if (filtros.dataUltimaAlteracaoFim) {
      url.searchParams.append('filtro[dataUltimaAlteracaoStatus][]', filtros.dataUltimaAlteracaoFim);
    }
    
    // Adicionar campos solicitados
    campos.forEach(campo => {
      url.searchParams.append('campos[]', campo);
    });
    
    // Adicionar limite
    url.searchParams.append('itens', limite.toString());
    
    // Gerar curl completo para teste no Postman (console)
    const curl = buildCurlChamados(url.toString());
    console.log('📋 cURL para testar no Postman (Sincronizar Chamados):\n', curl);
    
    // Fazer requisição para a API
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'accept': 'application/json, text/javascript, */*; q=0.01',
        'accept-language': 'pt-BR,pt;q=0.7',
        'authorization': API_AUTH_TOKEN,
        'content-type': 'application/json',
        'origin': 'https://intranet.prosperasistemas.com'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Erro na API: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // A API retorna: { message, data: { horas, itens } }
    let chamados: ChamadoAPI[] = [];
    
    if (data.data && data.data.itens && Array.isArray(data.data.itens)) {
      chamados = data.data.itens;
    } else if (Array.isArray(data)) {
      chamados = data;
    } else if (data.data && Array.isArray(data.data)) {
      chamados = data.data;
    } else if (data.items && Array.isArray(data.items)) {
      chamados = data.items;
    } else {
      console.error('❌ Formato de resposta inesperado:', data);
      throw new Error('Formato de resposta da API inválido');
    }
    
    // Processar e salvar cada chamado no banco
    const chamadosProcessados = [];
    
    for (const chamado of chamados) {
      try {
        // Preparar dados para inserção
        const chamadoData = {
          chamado_id: chamado.id,
          tipo_id: chamado.tipo?.id || '',
          tipo_descricao: chamado.tipo?.descricao || '',
          tipo_atendimento_id: chamado.tipoAtendimento?.id || '',
          tipo_atendimento_descricao: chamado.tipoAtendimento?.descricao || '',
          descricao: chamado.descricao || '',
          natureza_id: chamado.natureza?.id || '',
          natureza_descricao: chamado.natureza?.descricao || '',
          status_id: chamado.status?.id || '',
          status_descricao: chamado.status?.descricao || '',
          prioridade_id: chamado.prioridade?.id || '',
          prioridade_descricao: chamado.prioridade?.descricao || '',
          prioridade_cor: chamado.prioridade?.cor || '',
          data_inclusao: extractDate(chamado.incluido),
          data_alteracao: extractDate(chamado.alterado),
          oculto: chamado.oculto || false,
          data_conclusao: extractDate(chamado.dataConclusao),
          data_inicio_atendimento: extractDate(chamado.dataInicioAtendimento),
          chamado_vinculado_id: chamado.chamadoVinculado?.id || null,
          chamado_vinculado_solicitacao: chamado.chamadoVinculado?.solicitacao || null,
          empresa_id: chamado.empresa?.id || null,
          empresa_cnpj: chamado.empresa?.cnpj || '',
          empresa_nome: chamado.empresa?.nome || '',
          tempo_estimado: chamado.tempoEstimado || '00:00',
          horas_negociadas: chamado.horasNegociadas || '00:00',
          responsavel_id: chamado.responsavel?.id || null,
          responsavel_nome: chamado.responsavel?.nome || '',
          projeto_id: chamado.projeto?.id || null,
          projeto_nome: chamado.projeto?.nome || '',
          projeto_fechado: chamado.projeto?.fechado || null,
          projeto_categoria_id: chamado.projeto?.categoria?.id || null,
          projeto_categoria_nome: chamado.projeto?.categoria?.nome || '',
          produto_id: chamado.produto?.id || null,
          produto_nome: chamado.produto?.nome || '',
          produto_icone: chamado.produto?.icone || '',
          data_conclusao_prevista: extractDate(chamado.dataConclusaoPrevista),
          chamado_externo: chamado.chamadoExterno || null,
          departamentos: JSON.stringify(chamado.departamentos || []),
          sla_executado: chamado.sla?.executado || '',
          sla_prevista: chamado.sla?.prevista || '',
          sla_previstas: chamado.sla?.previstas || 0,
          sla_executados: chamado.sla?.executados || 0,
          sla_porcentagem: chamado.sla?.porcentagem || 0,
          ultima_movimentacao_data: extractDate(chamado.ultimaMovimentacao?.data),
          ultima_movimentacao_diferenca: chamado.ultimaMovimentacao?.diferenca || null,
          ultima_movimentacao_string: chamado.ultimaMovimentacao?.string || '',
          iniciado: chamado.iniciado || false,
          horas_executadas: chamado.horasExecutadas || '00:00',
          horas_faturadas: chamado.horasFaturadas || '00:00',
          data_ultima_alteracao_status: extractDate(chamado.dataUltimaAlteracaoStatus),
          ordens_servico: chamado.ordensServico || '',
          solicitacao: (() => {
            const root = chamado.solicitacao;
            if (root != null && String(root).trim() !== '') return String(root).trim();
            const vin = chamado.chamadoVinculado?.solicitacao;
            if (vin != null && String(vin).trim() !== '') return String(vin).trim();
            return null;
          })(),
          dados_api: chamado,
          ultima_sincronizacao: new Date().toISOString()
        };
        
        // Upsert (insert ou update se já existir)
        const { data: savedChamado, error } = await supabase
          .from('chamados')
          .upsert(chamadoData, {
            onConflict: 'chamado_id',
            ignoreDuplicates: false
          })
          .select()
          .single();
        
        if (error) {
          console.error(`❌ Erro ao salvar chamado ${chamado.id}:`, error);
        } else {
          chamadosProcessados.push(savedChamado);
        }
      } catch (error) {
        console.error(`❌ Erro ao processar chamado ${chamado.id}:`, error);
      }
    }
    
    return {
      sucesso: true,
      total: chamados.length,
      salvos: chamadosProcessados.length,
      chamados: chamadosProcessados
    };
  } catch (error) {
    console.error('❌ Erro na sincronização de chamados:', error);
    throw error;
  }
};

// Função para buscar chamados do banco de dados
export const fetchChamados = async (
  filtros?: ChamadosFiltros,
  options?: FetchChamadosOptions
) => {
  try {
    const lightweight = options?.lightweight ?? true;
    let query = supabase
      .from('chamados')
      .select(lightweight ? CHAMADOS_LIGHTWEIGHT_SELECT : '*')
      .order('data_inclusao', { ascending: false });
    
    // Aplicar filtros se fornecidos
    if (filtros?.status && filtros.status.length > 0) {
      query = query.in('status_id', filtros.status);
    }
    
    if (filtros?.tipo && filtros.tipo.length > 0) {
      query = query.in('tipo_id', filtros.tipo);
    }
    
    // in()/eq() são case-sensitive; ilike alinha com OS/relatórios (mesma empresa com grafia diferente no banco)
    if (filtros?.produto && filtros.produto.length > 0) {
      const list = filtros.produto.map((p) => p.trim()).filter((p) => p.length > 0);
      if (list.length === 1) {
        query = query.ilike('produto_nome', list[0]);
      } else if (list.length > 1) {
        const ors = list
          .map((v) => {
            const escaped = v.replace(/"/g, '""');
            return `produto_nome.ilike."${escaped}"`;
          })
          .join(',');
        query = query.or(ors);
      }
    }

    if (filtros?.empresa && filtros.empresa.length > 0) {
      const list = filtros.empresa.map((e) => e.trim()).filter((e) => e.length > 0);
      if (list.length === 1) {
        query = query.ilike('empresa_nome', list[0]);
      } else if (list.length > 1) {
        const ors = list
          .map((v) => {
            const escaped = v.replace(/"/g, '""');
            return `empresa_nome.ilike."${escaped}"`;
          })
          .join(',');
        query = query.or(ors);
      }
    }
    
    if (filtros?.responsavel && filtros.responsavel.length > 0) {
      query = query.in('responsavel_nome', filtros.responsavel);
    }
    
    if (filtros?.dataInicio) {
      query = query.gte('data_inclusao', filtros.dataInicio);
    }
    
    if (filtros?.dataFim) {
      // Incluir o dia inteiro: se for só YYYY-MM-DD, usar fim do dia
      const dataFimVal = filtros.dataFim.length <= 10 ? `${filtros.dataFim}T23:59:59.999Z` : filtros.dataFim;
      query = query.lte('data_inclusao', dataFimVal);
    }

    // Intervalo de data de conclusão: incluir chamados ainda sem conclusão (data_conclusao nula),
    // senão o padrão "mês atual" esconde tickets abertos sincronizados e a lista parece não atualizar.
    if (filtros?.dataConclusaoInicio && filtros?.dataConclusaoFim) {
      const dataConclusaoFimVal =
        filtros.dataConclusaoFim.length <= 10
          ? `${filtros.dataConclusaoFim}T23:59:59.999Z`
          : filtros.dataConclusaoFim;
      // Aspas no fim do intervalo: ISO com "." quebra o parser do PostgREST sem aspas
      query = query.or(
        `and(data_conclusao.gte.${filtros.dataConclusaoInicio},data_conclusao.lte."${dataConclusaoFimVal}"),data_conclusao.is.null`
      );
    } else {
      if (filtros?.dataConclusaoInicio) {
        query = query.gte('data_conclusao', filtros.dataConclusaoInicio);
      }
      if (filtros?.dataConclusaoFim) {
        const dataConclusaoFimVal =
          filtros.dataConclusaoFim.length <= 10
            ? `${filtros.dataConclusaoFim}T23:59:59.999Z`
            : filtros.dataConclusaoFim;
        query = query.lte('data_conclusao', dataConclusaoFimVal);
      }
    }

    // Filtro para tela de projeções: usar data_conclusao_prevista para reduzir o dataset carregado
    // (alocação usa data_conclusao_prevista e não data_conclusao).
    if (filtros?.dataConclusaoPrevistaInicio) {
      query = query.gte('data_conclusao_prevista', filtros.dataConclusaoPrevistaInicio);
    }
    if (filtros?.dataConclusaoPrevistaFim) {
      const dataPrevistaFimVal =
        filtros.dataConclusaoPrevistaFim.length <= 10
          ? `${filtros.dataConclusaoPrevistaFim}T23:59:59.999Z`
          : filtros.dataConclusaoPrevistaFim;
      query = query.lte('data_conclusao_prevista', dataPrevistaFimVal);
    }
    
    if (filtros?.search) {
      const searchTerm = filtros.search.trim();
      
      // Verificar se é um número para buscar por ID
      const isNumeric = /^\d+$/.test(searchTerm);
      
      if (isNumeric) {
        // Se for numérico, buscar por ID ou nos campos de texto que contenham o número
        const chamadoId = parseInt(searchTerm, 10);
        query = query.or(
          `chamado_id.eq.${chamadoId},descricao.ilike.%${searchTerm}%,projeto_nome.ilike.%${searchTerm}%,responsavel_nome.ilike.%${searchTerm}%,empresa_nome.ilike.%${searchTerm}%,produto_nome.ilike.%${searchTerm}%`
        );
      } else {
        // Se não for numérico, buscar apenas nos campos de texto
        query = query.or(
          `descricao.ilike.%${searchTerm}%,projeto_nome.ilike.%${searchTerm}%,responsavel_nome.ilike.%${searchTerm}%,empresa_nome.ilike.%${searchTerm}%,produto_nome.ilike.%${searchTerm}%`
        );
      }
    }

    query = query.limit(10000);

    const { data, error } = await query;
    
    if (error) {
      console.error('❌ Erro na query do Supabase:', error);
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('❌ Erro ao buscar chamados:', error);
    throw error;
  }
};

